import React, { useState, useEffect, useRef } from 'react';
import { FaChevronLeft, FaChevronRight, FaLayerGroup, FaAngleUp, FaAngleDown, FaSpinner } from 'react-icons/fa';
import { EnhancedTrailData } from '@/types/enhancedTrail';
import { useLanguage } from '@/context/LanguageContext';

interface TrailCarouselProps {
  trails: EnhancedTrailData[];
  onTrailSelect: (trail: EnhancedTrailData) => void;
  selectedTrail: EnhancedTrailData | null;
  onShowAllToggle: () => void;
  showAll: boolean;
  onNavigate?: (index: number, total: number) => void;
  isLoadingMore?: boolean;
}

const TrailCarousel: React.FC<TrailCarouselProps> = ({
  trails,
  onTrailSelect,
  selectedTrail,
  onShowAllToggle,
  showAll,
  onNavigate,
  isLoadingMore = false
}) => {
  const { translate } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animateHint, setAnimateHint] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Reset current index when trails change
  useEffect(() => {
    setCurrentIndex(0);
  }, [trails]);

  // Show animation hint for 5 seconds
  useEffect(() => {
    if (trails.length > 1 && animateHint) {
      const timer = setTimeout(() => {
        setAnimateHint(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [trails, animateHint]);

  // Auto-collapse carousel after 5 seconds of inactivity
  useEffect(() => {
    if (!isHovered && !isCollapsed) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setIsCollapsed(true);
      }, 5000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isHovered, isCollapsed]);

  // Add click outside listener to collapse carousel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (carouselRef.current && !carouselRef.current.contains(event.target as Node) && !isCollapsed) {
        setIsCollapsed(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCollapsed]);

  // Reset the auto-collapse timer when user interacts with carousel
  const resetCollapseTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (!isHovered) {
        setIsCollapsed(true);
      }
    }, 5000);
  };

  // Handle mouse enter/leave events
  const handleMouseEnter = () => {
    setIsHovered(true);
    // No longer auto-expand on hover
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    resetCollapseTimer();
  };

  // Toggle collapse state
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    if (!isCollapsed) {
      // If we're collapsing, no need to set a timer
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    } else {
      // If we're expanding, set a timer to collapse again
      resetCollapseTimer();
    }
  };

  // If we have no trails, don't render anything
  if (trails.length === 0) {
    return null;
  }

  // If showing all trails and no specific trail is selected, just show a minimized indicator
  if (showAll && !selectedTrail) {
    return (
      <div
        className="absolute bottom-5 left-1/2 transform -translate-x-1/2 z-10 bg-black bg-opacity-40 hover:bg-opacity-60 rounded-full shadow-md px-3 py-1.5 transition-all duration-300"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          onClick={onShowAllToggle}
          className="flex items-center text-xs text-white font-medium"
        >
          <span className="mr-1">{translate('trailDiscovery.showingAll', 'All Trails')}</span>
          <span className="text-blue-200">({trails.length})</span>
        </button>
      </div>
    );
  }

  // Navigate to previous trail
  const handlePrevious = () => {
    const newIndex = currentIndex === 0 ? trails.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);

    if (trails[newIndex]) {
      onTrailSelect(trails[newIndex]);
    }

    // Notify parent component about navigation (for progressive loading)
    if (onNavigate) {
      onNavigate(newIndex, trails.length);
    }

    // Don't expand carousel when arrows are clicked
    resetCollapseTimer();
  };

  // Navigate to next trail
  const handleNext = () => {
    const newIndex = currentIndex === trails.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);

    if (trails[newIndex]) {
      onTrailSelect(trails[newIndex]);
    }

    // Notify parent component about navigation (for progressive loading)
    if (onNavigate) {
      onNavigate(newIndex, trails.length);
    }

    // Don't expand carousel when arrows are clicked
    resetCollapseTimer();
  };

  // Get the currently displayed trail (either selected trail or the one at current index)
  const currentTrail = selectedTrail || trails[currentIndex];

  return (
    <div
      ref={carouselRef}
      className={`absolute bottom-5 left-1/2 transform -translate-x-1/2 z-10 transition-all duration-700 ${isCollapsed ? 'max-w-min' : 'max-w-lg w-full px-4'
        }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isCollapsed ? (
        // Collapsed state - only show arrows
        <div className="flex items-center space-x-2 bg-black bg-opacity-30 hover:bg-opacity-50 rounded-full p-1 transition-all duration-700">
          <button
            onClick={handlePrevious}
            className={`p-2 rounded-full hover:bg-white hover:bg-opacity-20 text-white transition-all ${trails.length <= 1 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            disabled={trails.length <= 1}
            title={translate('trailDiscovery.previous', 'Previous Trail')}
          >
            <FaChevronLeft />
          </button>
          <button
            onClick={toggleCollapse}
            className="p-2 rounded-full text-white hover:bg-white hover:bg-opacity-20 transition-all"
            title={translate('trailDiscovery.expand', 'Show Details')}
          >
            <FaAngleUp />
          </button>
          <button
            onClick={handleNext}
            className={`p-2 rounded-full hover:bg-white hover:bg-opacity-20 text-white transition-all ${trails.length <= 1 ? 'opacity-50 cursor-not-allowed' : ''
              } ${animateHint && trails.length > 1 ? 'animate-pulse' : ''}`}
            disabled={trails.length <= 1}
            title={translate('trailDiscovery.next', 'Next Trail')}
          >
            <FaChevronRight />
          </button>
        </div>
      ) : (
        // Expanded state - show full carousel
        <div className="relative bg-black bg-opacity-30 text-white rounded-lg shadow-md overflow-hidden transition-all duration-700">
          {/* Trail information overlay */}
          <div className="p-4">
            <div className="flex justify-between items-start mb-1">
              <h3 className="text-lg font-semibold">{currentTrail.name}</h3>
              <div className="flex items-center space-x-2">
                <span className="text-xs bg-white bg-opacity-20 rounded-full px-2 py-1">
                  {currentIndex + 1} / {trails.length}
                </span>
                <button
                  onClick={toggleCollapse}
                  className="p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-all"
                  title={translate('trailDiscovery.collapse', 'Hide Details')}
                >
                  <FaAngleDown />
                </button>
              </div>
            </div>
            <div className="flex space-x-4 text-sm mb-2">
              <span>{currentTrail.totalDistance} km</span>
              <span>{currentTrail.estimatedDuration} hrs</span>
              <span className="capitalize">{translate(`difficulty.${currentTrail.difficulty}`, currentTrail.difficulty)}</span>
            </div>
            <p className="text-sm text-gray-200 line-clamp-2">
              {currentTrail.description}
            </p>

            {/* Navigation buttons */}
            <div className="flex justify-between items-center mt-3">
              {/* Show all button */}
              <button
                onClick={onShowAllToggle}
                className="flex items-center text-xs bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full px-3 py-1.5 transition-all"
              >
                <FaLayerGroup className="mr-1" />
                {translate('trailDiscovery.showAllTrails', 'Show All')}
              </button>

              {/* Arrows */}
              <div className="flex space-x-2">
                <button
                  onClick={handlePrevious}
                  className={`p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all ${trails.length <= 1 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  disabled={trails.length <= 1}
                >
                  <FaChevronLeft />
                </button>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleNext}
                    className={`p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all ${trails.length <= 1 ? 'opacity-50 cursor-not-allowed' : ''
                      } ${animateHint && trails.length > 1 ? 'animate-pulse' : ''}`}
                    disabled={trails.length <= 1}
                  >
                    <FaChevronRight />
                  </button>
                  {isLoadingMore && (
                    <div className="animate-spin text-white ml-2">
                      <FaSpinner size={16} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Animated hint */}
          {animateHint && trails.length > 1 && (
            <div className="absolute top-0 right-0 bottom-0 left-0 pointer-events-none">
              <div className="absolute top-1/2 right-12 transform -translate-y-1/2 animate-bounce text-white">
                <div className="flex flex-col items-center">
                  <FaChevronRight size={24} className="mb-1" />
                  <span className="text-xs bg-black bg-opacity-70 rounded px-2 py-1">
                    {translate('trailDiscovery.swipeHint', 'Swipe through trails')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TrailCarousel;